/**
 * Unit intake wizard (SELL-03): steps, per-step validation, the inspection
 * checklist (PRD S-03) and the payloads sent to crud. Pure: no I/O, no React.
 */
import { rupeesToPaise } from './money';
import { isValidSerial, normalizeSerial } from './serial';
import { GRADES } from './catalog';
import { conditionErrors, gradeRequired } from '@/lib/conditions';
import { validateListing, listingPayload } from './listing';
import { attributesFromForm, validateAttributes, isImei } from '@/lib/productTypes';
import { uploadProblem } from './photos';

export const WIZARD_STEPS = [
  { key: 'basic', title: 'Product', description: 'Type & model' },
  { key: 'specs', title: 'Specifications', description: 'Unit & serial' },
  { key: 'inspection', title: 'Condition & inspection', description: 'Checklist' },
  { key: 'photos', title: 'Photos', description: 'Gallery' },
  { key: 'publish', title: 'Publish', description: 'Listing' },
];

export const BATTERY_METHODS = [
  { value: 'os_report', label: 'OS battery report' },
  { value: 'bios', label: 'BIOS / UEFI diagnostics' },
  { value: 'vendor_tool', label: 'Vendor diagnostic tool' },
  { value: 'load_test', label: 'Timed load test' },
];

/**
 * The checklist items for a product type (registry checklists); none without a known type
 * (no laptop default, ADR 0009 §1): the server refuses to inspect an untyped unit anyway.
 */
export function checklistFor(type, checklists = {}) {
  return (type && checklists[type.checklist]) || [];
}

/**
 * Stocked by quantity (D60): a type sold by quantity, unless the seller chose to
 * record this one as a single serialised unit. A lot has no serial and no
 * inspection (D61); it carries a quantity on hand.
 */
export const canStockByQty = (type) => Boolean(type) && type.stock === 'quantity';
export const isLotForm = (f, type) => canStockByQty(type) && f.stockMode !== 'unit';

export function emptyInspection(items = []) {
  return Object.fromEntries(items.map((i) => [i.key, { result: '', note: '' }]));
}

export function initialWizardForm() {
  return {
    modelMode: 'existing',
    product_model_id: '',
    // No default type (ADR 0009 §1): the wizard picks the store's first type once the registry loads.
    product_type: '', specs: {},
    brand_id: '', brand: '', series: '', model_name: '', category_id: '',
    internal_sku: '', serial: '', grade: '', condition: '', stockMode: 'quantity', qty: '1',
    battery_health_pct: '', battery_method: '', acquisition_cost: '', accessories: '', notes: '',
    inspection: emptyInspection(), technician: '',
    photos: [],
    illustrative: [], illustrativeAlt: '', // MERCH-77: generated or stock images, never counted
    createListing: true, title: '', price: '', mrp: '', warranty_months: '6', listingStatus: 'draft',
  };
}

/** Safety checks that have not passed (any safety item not explicitly "pass"). */
export function inspectionBlockers(inspection = {}, items = []) {
  return items.filter((i) => i.safety && (inspection[i.key] || {}).result !== 'pass').map((i) => i.label);
}

/** The server's readiness rule (lib/catalog/readiness MIN_UNIT_PHOTOS): photos of the exact unit before publish. */
export const MIN_UNIT_PHOTOS = 3;
export const MAX_UNIT_PHOTOS = 12;
/** The photos step hint for a serialised unit with fewer than MIN_UNIT_PHOTOS. */
export const photosHint = (n) => (n >= MIN_UNIT_PHOTOS ? '' : `${MIN_UNIT_PHOTOS} photos needed to publish (${n} added). You can save the unit to stock without them.`);
/** What to photograph, so shoppers see the real unit (MERCH-54). */
export const PHOTO_SHOTS = ['The whole item from the front', 'Powered on or in use, where it has a screen or controls', 'Ports, labels and any wear or marks, close up'];

/**
 * The publish checklist the wizard can know before anything is saved — the same
 * rules the server applies (Listing readiness action) for a new unit.
 * → [{ key, label, ok, detail }]
 */
export function wizardReadiness(f, ctx = {}) {
  const type = ctx.type || null;
  const items = ctx.items || [];
  if (isLotForm(f, type)) {
    const ok = isInt(f.qty, 1, 100000);
    return [{ key: 'lot_stock', label: 'Stock on hand', ok, detail: ok ? '' : 'Enter the quantity in stock' }];
  }
  const marked = items.every((i) => ['pass', 'fail'].includes(((f.inspection || {})[i.key] || {}).result)) && Boolean(String(f.technician || '').trim());
  const blockers = inspectionBlockers(f.inspection, items);
  const n = (f.photos || []).length;
  return [
    { key: 'inspection', label: 'Inspection recorded', ok: marked, detail: marked ? '' : 'Mark every check and name the technician' },
    { key: 'safety', label: 'No failed safety check', ok: blockers.length === 0, detail: blockers.length ? `Not passed: ${blockers.join(', ')}` : '' },
    { key: 'photos', label: `At least ${MIN_UNIT_PHOTOS} photos of this exact unit`, ok: n >= MIN_UNIT_PHOTOS, detail: n >= MIN_UNIT_PHOTOS ? '' : `${n} of ${MIN_UNIT_PHOTOS} added` },
  ];
}

const isInt = (v, min, max) => /^\d+$/.test(String(v)) && Number(v) >= min && Number(v) <= max;

/**
 * Errors for one step: { field: message }. Empty object = the step is valid.
 * `ctx.canPublish` = the user holds catalog:publish; `ctx.type` = the product
 * type (registry entry) of the unit's model, `ctx.items` its checklist.
 */
export function validateStep(stepKey, f, ctx = {}) {
  const e = {};
  const type = ctx.type || null;
  const items = ctx.items || [];
  const hasBattery = !type || type.battery;
  if (stepKey === 'basic') {
    if (f.modelMode === 'existing') {
      if (!f.product_model_id) e.product_model_id = 'Pick a model, or create a new one';
    } else {
      if (!f.product_type) e.product_type = 'Pick a product type';
      if (!f.brand_id) e.brand_id = 'Pick a brand, or type a new one to add it';
      if (!String(f.model_name).trim()) e.model_name = 'Model is required';
    }
  }
  if (stepKey === 'specs') {
    // A new model's specs come from its type's registry attributes, whatever the type.
    if (f.modelMode === 'new' && type) {
      for (const [k, msg] of Object.entries(validateAttributes(type, f.specs))) e[`specs.${k}`] = msg;
    }
    if (!String(f.internal_sku).trim()) e.internal_sku = 'SKU is required';
    if (isLotForm(f, type)) {
      if (!isInt(f.qty, 1, 100000)) e.qty = 'Quantity is a whole number from 1 to 100000';
    } else if (type && type.serial === 'imei') {
      if (!isImei(f.serial)) e.serial = 'Enter the 15-digit IMEI (dial *#06# on the device)';
    } else if (!normalizeSerial(f.serial)) e.serial = 'Serial number is required';
    else if (!isValidSerial(f.serial)) e.serial = 'Serial must be 5–30 letters or digits';
    // Condition and grade from the registry (ctx.conditions, D98): a grade only where the condition needs one.
    Object.assign(e, conditionErrors(f, { conditions: ctx.conditions, type, grades: GRADES }));
    if (hasBattery && !isLotForm(f, type) && f.battery_health_pct !== '' && !isInt(f.battery_health_pct, 0, 100)) e.battery_health_pct = 'Battery health is 0–100%';
    if (hasBattery && !isLotForm(f, type) && f.battery_health_pct !== '' && !f.battery_method) e.battery_method = 'Say how battery health was measured';
    if (f.acquisition_cost !== '' && rupeesToPaise(f.acquisition_cost) == null) e.acquisition_cost = 'Enter an amount in rupees';
  }
  if (stepKey === 'inspection' && !isLotForm(f, type)) {
    for (const item of items) {
      const r = (f.inspection || {})[item.key] || {};
      if (r.result !== 'pass' && r.result !== 'fail') e[`inspection.${item.key}`] = 'Mark pass or fail';
      else if (r.result === 'fail' && !String(r.note || '').trim()) e[`inspection.${item.key}`] = 'Describe the failure';
    }
    if (!String(f.technician || '').trim()) e.technician = 'Technician name is required';
  }
  if (stepKey === 'photos') {
    const n = (f.photos || []).length;
    // Fewer than MIN_UNIT_PHOTOS only blocks publishing (publish step), never saving the unit to stock (MERCH-54).
    if (n > MAX_UNIT_PHOTOS) e.photos = `At most ${MAX_UNIT_PHOTOS} photos per unit`;
    const extra = (f.illustrative || []).length;
    const problem = extra ? uploadProblem('illustrative', { count: extra, alt: f.illustrativeAlt }) : '';
    if (problem) e.illustrative = problem;
  }
  if (stepKey === 'publish' && f.createListing) {
    const blockers = isLotForm(f, type) ? [] : inspectionBlockers(f.inspection, items);
    const le = validateListing({ ...f, status: f.listingStatus }, { canPublish: ctx.canPublish, blockers });
    const { status, ...rest } = le;
    Object.assign(e, rest, status ? { listingStatus: status } : {});
    // A serialised unit is sold as itself: shoppers see its real photos (MERCH-54). Lots are boxed stock.
    const n = (f.photos || []).length;
    if (!e.listingStatus && f.listingStatus === 'published' && !isLotForm(f, type) && n < MIN_UNIT_PHOTOS) {
      e.listingStatus = `Publishing needs at least ${MIN_UNIT_PHOTOS} genuine photos of this exact unit (${n} added). Save as a draft, or go back to Photos.`;
    }
  }
  return e;
}

/** First step (index) with errors, or -1 when every step is valid. */
export function firstInvalidStep(f, ctx) {
  return WIZARD_STEPS.findIndex((s) => Object.keys(validateStep(s.key, f, ctx)).length > 0);
}

const intOrNull = (v) => (v === '' || v == null ? null : Number(v));

/** crud payloads for the three creates (model only when modelMode is "new"). `ctx.type` as in validateStep. */
export function buildPayloads(f, ctx = {}) {
  const type = ctx.type || null;
  const lot = isLotForm(f, type);
  const hasBattery = (!type || type.battery) && !lot;
  // A new model names its type and its specs as attributes; the server validates both (no default type).
  const model = f.modelMode === 'new'
    ? {
        brand_id: intOrNull(f.brand_id), brand: f.brand.trim(), series: f.series.trim() || null, model_name: f.model_name.trim(),
        category_id: intOrNull(f.category_id), product_type: type ? type.key : f.product_type || null, attributes: attributesFromForm(type, f.specs),
      }
    : null;
  const notes = [
    f.notes && f.notes.trim(),
    f.accessories && `Accessories: ${f.accessories.trim()}`,
    hasBattery && f.battery_method && `Battery measured by: ${f.battery_method}`,
  ].filter(Boolean).join('\n');
  const unit = {
    product_model_id: f.modelMode === 'existing' ? Number(f.product_model_id) : null,
    internal_sku: f.internal_sku.trim().toUpperCase(),
    ...(lot ? { stock_mode: 'quantity', qty_on_hand: Number(f.qty) } : { serial: normalizeSerial(f.serial) }),
    // A grade only where the condition has one (D98); without the registry, as entered.
    grade: (ctx.conditions ? gradeRequired(ctx.conditions, f.condition, ctx.type || null) : true) ? f.grade || null : null,
    condition: f.condition,
    battery_health_pct: hasBattery ? intOrNull(f.battery_health_pct) : null,
    acquisition_cost_paise: rupeesToPaise(f.acquisition_cost) || 0,
    stock_state: 'in_stock',
    notes: notes || null,
  };
  const listing = f.createListing ? listingPayload({ ...f, status: f.listingStatus }) : null;
  return { model, unit, listing };
}
